import clsx from "clsx";
import {
  FC,
  memo,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { VirtualList } from "@tsmono/react/virtual";
import type { VirtualListHandle } from "@tsmono/react/virtual";
import { getOwn } from "@tsmono/util";

import { GeneratingIndicator } from "../indicators/GeneratingIndicator";
import { LoadingEventsIndicator } from "../indicators/LoadingEventsIndicator";
import type { ListRowSelection } from "../selection/rowSelection";
import {
  RowToggleContext,
  SelectableRow,
} from "../selection/RowSelectionContext";

import { EventLabelContext } from "./EventLabelContext";
import { eventSearchText } from "./eventText";
import {
  computeHasToolEventsAtDepth,
  computeToolEventIdsAtLevel,
} from "./hasToolEventsAtDepth";
import { isSelectableEvent } from "./selection/transcriptSelection";
import { RenderedEventNode } from "./TranscriptVirtualList";
import styles from "./TranscriptVirtualListComponent.module.css";
import { computeVisualActionContext } from "./transcriptVisualActions";
import { kTranscriptScrollPaddingStart } from "./turnNavigation";
import { EventNode, EventNodeContext, EventPanelCallbacks } from "./types";

interface TranscriptVirtualListComponentProps {
  id: string;
  listHandle: RefObject<VirtualListHandle | null>;
  eventNodes: EventNode[];
  initialEventId?: string | null;
  /** This mount landed on a deep link / exit-focus target: live-tail follow
   *  stands down so the landing wins (forwarded to VirtualList). */
  navOwned?: boolean;
  /** Explicit `follow=1` URL param: arm live-tail at mount despite navOwned. */
  followRequested?: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
  running?: boolean;
  backfilling?: boolean;
  /** Whether a live→finished transition may scroll the view to the top.
   *  Hosts pass false for unsuccessful finishes (error/cancelled): the
   *  error panel renders at the bottom, where the user is looking. */
  scrollToTopOnFinish?: boolean;
  className?: string;
  turnMap?: Map<string, { turnNumber: number; totalTurns: number }>;
  /** Indent rows relative to the first row's depth instead of the absolute
   *  transcript depth — for the focus page, whose slice can start inside an
   *  agent span and would otherwise inherit that span's indentation. */
  relativeIndent?: boolean;
  disableVirtualization?: boolean;
  onNativeFindChanged?: (nativeFind: boolean) => void;
  onAutoCollapse?: (eventId: string) => void;
  renderAgentCard?: (node: EventNode, className?: string) => ReactNode;
  eventCallbacks?: EventPanelCallbacks;
  /** Extra context fields merged into every EventNodeContext entry. */
  eventNodeContext?: Partial<EventNodeContext>;
  /** Evidence selection, present while selection mode is on: each non-structural
   *  row gets a header checkbox. */
  selection?: ListRowSelection;
  /** External ref filled with the virtual list's visible range, for find machinery. */
  visibleRangeRef?: RefObject<{ startIndex: number; endIndex: number }>;
}

/**
 * Renders the Transcript component.
 */
export const TranscriptVirtualListComponent: FC<
  TranscriptVirtualListComponentProps
> = ({
  id,
  listHandle,
  eventNodes,
  scrollRef,
  running,
  backfilling,
  scrollToTopOnFinish = true,
  initialEventId,
  navOwned,
  followRequested,
  className,
  turnMap,
  relativeIndent,
  disableVirtualization,
  onNativeFindChanged,
  onAutoCollapse,
  renderAgentCard,
  eventCallbacks,
  eventNodeContext,
  selection,
  visibleRangeRef,
}) => {
  // Always virtualize when not explicitly disabled. The previous threshold
  // (`running || eventNodes.length > 100`) skipped virtualization for short
  // transcripts, which routed scroll-to-event through a plain-DOM
  // `scrollIntoView` fallback that didn't reliably scroll the actual scroll
  // container — making swimlane / outline navigation appear broken on small
  // event lists. VirtualList handles short lists fine.
  const useVirtualization = !disableVirtualization;

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    onNativeFindChanged?.(!useVirtualization);
  }, [onNativeFindChanged, useVirtualization]);

  // Mount-time anchor for the virtual list's layout. Captured once and frozen —
  // runtime URL→event navigation is handled imperatively in
  // TranscriptViewNodes, so this state never updates after the first render.
  const [initialEventIndex] = useState<number | undefined>(() => {
    if (initialEventId === null || initialEventId === undefined)
      return undefined;
    const idx = eventNodes.findIndex((e) => e.id === initialEventId);
    return idx === -1 ? undefined : idx;
  });

  // Pre-compute, in O(n), whether each event has a tool event at its depth.
  // This was previously an O(n^2) per-index backward scan run once per node
  // while building contextMap, which dominated time-to-first-paint on large or
  // deeply nested transcripts. computeHasToolEventsAtDepth returns the
  // identical boolean for every index (locked by hasToolEventsAtDepth.test.ts).
  const hasToolEventsLookup = useMemo(
    () => computeHasToolEventsAtDepth(eventNodes),
    [eventNodes]
  );
  const toolEventIdsLookup = useMemo(
    () => computeToolEventIdsAtLevel(eventNodes),
    [eventNodes]
  );

  // Non-virtual scroll-into-view for initial event
  const nonVirtualGridRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (!useVirtualization && initialEventId) {
      const row = nonVirtualGridRef.current?.querySelector(
        `[id="${initialEventId}"]`
      );
      row?.scrollIntoView({ block: "start" });
    }
  }, [initialEventId, useVirtualization]);

  // Pre-compute context objects for all event nodes to maintain stable references
  const contextMap = useMemo(() => {
    const map = new Map<string, EventNodeContext>();
    // A turn's first flattened event is its "capstone": the only header that
    // shows the turn-nav cluster while unstuck (followers show it only when
    // their header is pinned), so the cluster isn't duplicated down the turn.
    let prevTurnNumber: number | undefined;
    for (const [i, node] of eventNodes.entries()) {
      const hasToolEvents = hasToolEventsLookup[i] ?? false;
      const turnInfo = turnMap?.get(node.id);
      const { inputScreenshot, selfAnnotation } = computeVisualActionContext(
        eventNodes,
        i
      );
      const turnIsAnchor =
        turnInfo !== undefined && turnInfo.turnNumber !== prevTurnNumber;
      map.set(node.id, {
        hasToolEvents,
        toolEventIds: toolEventIdsLookup[i],
        turnInfo,
        turnIsAnchor,
        ...eventNodeContext,
        inputScreenshot,
        selfAnnotation,
      });
      prevTurnNumber = turnInfo?.turnNumber;
    }
    return map;
  }, [
    eventNodes,
    hasToolEventsLookup,
    toolEventIdsLookup,
    turnMap,
    eventNodeContext,
  ]);

  const eventLabels = eventNodeContext?.eventLabels;

  const renderRow = useCallback(
    (index: number, item: EventNode) => {
      const depth = relativeIndent
        ? item.depth - (eventNodes[0]?.depth ?? 0)
        : item.depth;

      const previousIndex = index - 1;
      const nextIndex = index + 1;
      const previous =
        previousIndex >= 0 && previousIndex < eventNodes.length
          ? eventNodes[previousIndex]
          : undefined;
      const next =
        nextIndex < eventNodes.length ? eventNodes[nextIndex] : undefined;
      const attached =
        item.event.event === "tool" &&
        (previous?.event.event === "tool" || previous?.event.event === "model");

      const attachedParent =
        item.event.event === "model" && next?.event.event === "tool";
      const attachedClass = attached ? styles.attached : undefined;
      const attachedChildClass = attached ? styles.attachedChild : undefined;
      const attachedParentClass = attachedParent
        ? styles.attachedParent
        : undefined;
      const depthRootClass = depth === 0 ? styles.depthRoot : undefined;

      const context = contextMap.get(item.id);
      const isLast = index === eventNodes.length - 1;
      const selectableRowId =
        selection && isSelectableEvent(item.event) ? item.id : undefined;
      const rowSelected =
        selection !== undefined &&
        selectableRowId !== undefined &&
        selection.selectedIds.has(item.id);
      const renderedNode = (
        <EventLabelContext.Provider value={getOwn(eventLabels, item.id)}>
          <SelectableRow id={selectableRowId} selected={rowSelected}>
            <RenderedEventNode
              node={item}
              next={next}
              className={clsx(
                attachedParentClass,
                attachedChildClass,
                depthRootClass
              )}
              context={context}
              onAutoCollapse={onAutoCollapse}
              renderAgentCard={renderAgentCard}
              eventCallbacks={eventCallbacks}
            />
          </SelectableRow>
        </EventLabelContext.Provider>
      );

      return (
        <div
          id={item.id}
          key={item.id}
          className={clsx(
            styles.node,
            isLast ? styles.last : undefined,
            attachedClass
          )}
          style={{
            paddingLeft: `${depth <= 1 ? depth * 0.7 : (0.7 + depth - 1) * 1}em`,
            // The right inset visually belongs to the transcript's nested card
            // chrome; the focus page strips cards, so indent left-only there.
            paddingRight: relativeIndent || depth === 0 ? undefined : "0.7em",
          }}
        >
          {renderedNode}
        </div>
      );
    },
    [
      eventNodes,
      contextMap,
      onAutoCollapse,
      renderAgentCard,
      eventCallbacks,
      eventLabels,
      relativeIndent,
      selection,
    ]
  );

  // Tools are executing when the latest model event requested tool calls that
  // don't yet all have a (completed) tool event. Pending tool events aren't
  // reliably streamed to the viewer, so we derive this from model events —
  // matching each tool_call to its tool event by id.
  const isBackfilling = backfilling === true;
  const toolsRunning = useMemo(
    () =>
      running === true && !isBackfilling && transcriptToolsRunning(eventNodes),
    [running, isBackfilling, eventNodes]
  );
  const showFooter = isBackfilling || toolsRunning;
  const Footer = useCallback(
    () => renderTranscriptFooter({ backfilling: isBackfilling, toolsRunning }),
    [isBackfilling, toolsRunning]
  );
  const components = useMemo(() => ({ Footer }), [Footer]);

  const list = useVirtualization ? (
    <VirtualList<EventNode>
      ref={listHandle}
      className={className}
      persistenceKey={id}
      scrollRef={scrollRef}
      data={eventNodes}
      initialIndex={initialEventIndex}
      navOwned={navOwned}
      followRequested={followRequested}
      scrollPaddingStart={kTranscriptScrollPaddingStart}
      renderRow={renderRow}
      live={running === true}
      smoothScroll={running === true && !isBackfilling}
      scrollToTopOnFinish={scrollToTopOnFinish}
      itemSearchText={eventSearchText}
      findScope="none"
      showProgress={showFooter}
      components={components}
      onVisibleRangeChange={(range) => {
        if (visibleRangeRef) visibleRangeRef.current = range;
      }}
    />
  ) : (
    <div ref={nonVirtualGridRef}>
      {eventNodes.map((node, index) => {
        const row = renderRow(index, node);
        return row;
      })}
      {renderTranscriptFooter({ backfilling: isBackfilling, toolsRunning })}
    </div>
  );

  return (
    <RowToggleContext.Provider value={selection?.onToggle}>
      {list}
    </RowToggleContext.Provider>
  );
};

// Memoized here (not in TranscriptVirtualList.tsx, which re-exports it) to avoid a circular import via RenderedEventNode.
export const TranscriptVirtualList = memo(TranscriptVirtualListComponent);
TranscriptVirtualList.displayName = "TranscriptVirtualList";

export const renderTranscriptFooter = ({
  backfilling,
  toolsRunning,
}: {
  backfilling: boolean;
  toolsRunning: boolean;
}): ReactElement | null => {
  if (backfilling) {
    return (
      <div className={styles.runningTool}>
        <LoadingEventsIndicator label="Loading events" />
      </div>
    );
  }
  if (toolsRunning) {
    return (
      <div className={styles.runningTool}>
        <GeneratingIndicator label="running" />
      </div>
    );
  }
  return null;
};

// True when the most recent model event requested tool calls that don't all
// have a completed tool event yet (i.e. a tool is still executing).
function transcriptToolsRunning(eventNodes: EventNode[]): boolean {
  let lastModelIdx = -1;
  for (let i = eventNodes.length - 1; i >= 0; i--) {
    if (eventNodes[i]?.event.event === "model") {
      lastModelIdx = i;
      break;
    }
  }
  if (lastModelIdx === -1) return false;
  const modelEvent = eventNodes[lastModelIdx]!.event;
  if (modelEvent.event !== "model" || modelEvent.pending) return false;
  const toolCalls = modelEvent.output.choices[0]?.message.tool_calls ?? [];
  if (toolCalls.length === 0) return false;
  const completedToolIds = new Set<string>();
  for (let i = lastModelIdx + 1; i < eventNodes.length; i++) {
    const ev = eventNodes[i]!.event;
    if (ev.event === "tool" && !ev.pending) completedToolIds.add(ev.id);
  }
  return toolCalls.some((call) => !completedToolIds.has(call.id));
}
